import { z } from 'zod'
import type { ProductType, PurchaseVariantLookup } from './catalog.js'
import { issuedChequeInputSchema } from './cheques.js'
import {
  localeSchema,
  moneySchema,
  optionalText,
  pageQuerySchema,
  quantitySchema,
  versionSchema,
} from './common.js'
import {
  type CostObjectProjectRef,
  type CostObjectTag,
  projectTagFields,
  refineProjectTag,
} from './cost-objects.js'
import type { OrgUnitRef } from './org-units.js'
import {
  type AgingBucket,
  type DocumentChequeRef,
  type DocumentLine,
  type DrawnRefund,
  discountTypeSchema,
  type InvoiceStatus,
  type LineUnit,
  lineUnitIdSchema,
  type PaymentMethod,
  type PaymentMethodRef,
  type PaymentStatus,
  paymentMethodIdSchema,
  paymentMethodSchema,
  paymentStatusSchema,
  type UserRef,
} from './sales.js'
import { lineSerialsSchema } from './serials.js'
import type { SupplierOption } from './suppliers.js'
import type { DocumentApproval, WorkflowRunStatus } from './workflows.js'

/**
 * Purchasing and payables (ASM-023). A purchase is the supplier's bill for goods received: posting it
 * moves the goods into stock and recognises the liability in one step. Supplier payments and supplier
 * returns/allowances settle purchases through AP allocations, mirroring receivables.
 */

export interface SupplierRef {
  id: string
  code: string
  name: string
  nameAr: string | null
}

const positiveQuantity = quantitySchema.refine((v) => Number(v) > 0, 'Quantity must be above zero')
const positiveAmount = moneySchema.refine((v) => Number(v) > 0, 'Enter an amount above zero')
const optionalId = z
  .uuid()
  .nullish()
  .transform((v) => v ?? null)

/** Documents created (recorded) by this user; `me` is the caller. */
const createdByFilterSchema = z.union([z.literal('me'), z.uuid()]).optional()

/** `?format=csv&lang=` of every purchasing export: the list's filters, view and sort, no paging. */
const exportFields = { format: z.enum(['csv']).default('csv'), lang: localeSchema.default('en') }

/**
 * The most rows one export of a purchasing list returns (purchases, supplier payments, returns,
 * refunds); a larger filtered set is refused (422 `EXPORT_TOO_LARGE`).
 */
export const PURCHASING_EXPORT_MAX_ROWS = 50_000

// ── purchases ──────────────────────────────────────────────────────────────────

export const purchaseLineInputSchema = z
  .object({
    variantId: optionalId,
    /** Defaults to the product name for product lines; required for free-text lines. */
    description: optionalText(500),
    quantity: positiveQuantity,
    /** A pack of the product (bought by the carton) or its base unit (default); stock receives quantity × factor. */
    unitId: lineUnitIdSchema,
    /** Cost per unit on the document's price basis (VAT-inclusive when the purchase is). */
    unitPrice: moneySchema.refine((v) => Number(v) >= 0, 'Price cannot be negative'),
    discountType: discountTypeSchema.default('PER_UNIT'),
    discountValue: moneySchema.default('0'),
    /** Omitted: the product's tax rate. null: no tax. A uuid: that rate. */
    taxRateId: z.uuid().nullable().optional(),
    /**
     * Services and free-text lines only: the expense (or asset) account the line is charged to.
     * null: the tenant's operating-expense account. Goods always go to inventory.
     */
    expenseAccountId: optionalId,
    /** One per unit for a serial-tracked product (exactly the quantity when posting); refused otherwise. */
    serials: lineSerialsSchema,
    /**
     * The project the line's cost belongs to (projects/02 §3–4). On a goods line it means "delivered to
     * the project site": posting moves no stock and charges the project's cost account instead of
     * inventory. Serial-tracked goods can't be tagged (422 PROJECT_SERIAL_GOODS). Frozen once posted.
     */
    ...projectTagFields,
  })
  .refine((l) => l.variantId !== null || Boolean(l.description), {
    path: ['description'],
    message: 'Describe the line or choose a product',
  })
  .superRefine(refineProjectTag)
export type PurchaseLineInput = z.input<typeof purchaseLineInputSchema>

const purchaseFields = z.object({
  /**
   * Optional on a draft (G25): staff without supplier rights record what arrived and someone holding
   * `suppliers.read` adds the supplier; posting and submitting need one (422 SUPPLIER_REQUIRED). Create:
   * omitted or null = none. Update: omitted keeps the current supplier, null clears it (refused while an
   * approval holds the draft). Setting or changing it needs `suppliers.read` (403 SUPPLIER_HIDDEN).
   */
  supplierId: z.uuid().nullish(),
  purchaseDate: z.iso.date(),
  /** Defaults to purchase date + the tenant's payment terms. */
  dueDate: z.iso
    .date()
    .nullish()
    .transform((v) => v ?? null),
  /** The supplier's own invoice number. */
  supplierReference: optionalText(100),
  /** Where the goods are received (default: the main warehouse at posting). */
  warehouseId: optionalId,
  /**
   * The requesting org unit. Create: omitted = the creator's home unit, null = none. Update: omitted
   * keeps the current unit, only an explicit null clears it. A newly chosen unit must be active.
   */
  orgUnitId: z.uuid().nullish(),
  notes: optionalText(2000),
  lines: z.array(purchaseLineInputSchema).min(1, 'Add at least one line').max(500),
})
const dueNotBeforePurchase = {
  path: ['dueDate'],
  message: 'The due date cannot be before the purchase date',
}
const dueOk = (v: { purchaseDate: string; dueDate: string | null }) =>
  v.dueDate === null || v.dueDate >= v.purchaseDate
export const purchaseInputSchema = purchaseFields.refine(dueOk, dueNotBeforePurchase)
export type PurchaseInput = z.input<typeof purchaseInputSchema>
export const purchaseUpdateSchema = purchaseFields
  .extend({ version: versionSchema })
  .refine(dueOk, dueNotBeforePurchase)
export type PurchaseUpdate = z.input<typeof purchaseUpdateSchema>

/**
 * `GET /purchasing/purchases`. `q` matches the number and the supplier's reference (substring,
 * case-insensitive), the supplier's code, and the supplier's name and Arabic name with Arabic letter
 * variants folded (as the sales lists). Sort keys: PURCHASE_SORT_FIELDS (default `purchaseDate`).
 * Without `suppliers.read` (G26): `q` matches the number and the reference only, and `supplierId` or the
 * `supplier` sort is refused (403 SUPPLIER_HIDDEN).
 */
export const purchaseListQuerySchema = pageQuerySchema.extend({
  status: z.enum(['DRAFT', 'POSTED', 'VOID']).optional(),
  /** Posted purchases with an open balance. */
  open: z.stringbool().optional(),
  /**
   * Posted purchases whose derived payment status (the list's `paymentStatus`) is this one; case does
   * not matter. OVERDUE: balance above zero and due date before today (Asia/Muscat).
   */
  paymentStatus: z.string().trim().toUpperCase().pipe(paymentStatusSchema).optional(),
  supplierId: z.uuid().optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  /** Purchases created by this user; `me` is the caller. */
  createdBy: createdByFilterSchema,
  /** Purchases of this org unit or any unit below it. */
  orgUnitId: z.uuid().optional(),
  /** Purchases with at least one line tagged to this project. */
  projectId: z.uuid().optional(),
})
export type PurchaseListQuery = z.input<typeof purchaseListQuerySchema>

/**
 * Sort keys of the purchase list and export. `balance` is the open balance (0 for drafts and void
 * purchases); `supplier` the supplier's name. Ties: newest first.
 */
export const PURCHASE_SORT_FIELDS = [
  'purchaseDate',
  'dueDate',
  'number',
  'total',
  'balance',
  'supplier',
] as const

/**
 * The purchase list's view tabs (pages/purchasing.md §7.3), each the list call beside it:
 *   all      no view filter
 *   drafts   `status=DRAFT` (the open orders, Q1)
 *   open     `open=true` (posted, balance above zero; overdue included)
 *   overdue  `paymentStatus=OVERDUE`
 *   void     `status=VOID`
 */
export const PURCHASE_VIEWS = ['all', 'drafts', 'open', 'overdue', 'void'] as const
export type PurchaseView = (typeof PURCHASE_VIEWS)[number]

/** `GET /purchasing/purchases/summary`: the list's filters other than the view, paging and sort. */
export const purchaseSummaryQuerySchema = purchaseListQuerySchema.pick({
  q: true,
  supplierId: true,
  from: true,
  to: true,
  createdBy: true,
  orgUnitId: true,
  projectId: true,
})
export type PurchaseSummaryQuery = z.input<typeof purchaseSummaryQuerySchema>

/**
 * Counts per view for the current filters, from one grouped query. Each `count` equals the `total`
 * of that view's list call with the same filters; `balance` is the sum of the listed rows' `balance`.
 * As of `asOf`, today in Asia/Muscat.
 */
export interface PurchaseSummary {
  asOf: string
  all: { count: number }
  drafts: { count: number }
  open: { count: number; balance: string }
  overdue: { count: number; balance: string }
  void: { count: number }
}

/** `GET /purchasing/purchases/export` (purchasing.export): the list's filters, view and sort, as CSV. */
export const purchaseExportQuerySchema = purchaseListQuerySchema
  .omit({ page: true, pageSize: true })
  .extend(exportFields)
export type PurchaseExportQuery = z.input<typeof purchaseExportQuerySchema>

/**
 * `GET /purchasing/purchases/duplicates`: the duplicate supplier-bill check (PO-H4, Q15). Purchases
 * of the supplier whose supplier's reference equals this one (trimmed, case-insensitive), drafts and
 * posted, never void ones; `excludeId` leaves out the purchase being edited or posted. A warning for
 * the editor and the post confirmation, never a block.
 */
export const purchaseDuplicateQuerySchema = z.object({
  supplierId: z.uuid(),
  supplierReference: z.string().trim().min(1).max(100),
  excludeId: z.uuid().optional(),
})
export type PurchaseDuplicateQuery = z.input<typeof purchaseDuplicateQuerySchema>

/** A purchase already carrying the supplier's reference; newest first, at most 10. */
export interface PurchaseDuplicate {
  id: string
  number: string | null
  status: 'DRAFT' | 'POSTED'
  purchaseDate: string
  supplierReference: string
  total: string
}

// ── a purchase read from a list or a supplier's bill (AC1; docs/refactor/ASSISTANT_CAPTURE.md) ───────────

/** A capture carries at most this many lines; `truncated` says the document had more. */
export const PURCHASE_CAPTURE_MAX_LINES = 100

/**
 * What a document read is (ASSISTANT_CAPTURE.md decision 28): a supplier's bill (a tax or cash invoice), a
 * credit note, a quotation (or proforma, or offer), a delivery note, or any other paper with item lines.
 * Only a bill is owed: the others are filled but flagged (`CREDIT_NOTE`, `NOT_A_BILL`).
 */
export const PURCHASE_DOCUMENT_KINDS = [
  'INVOICE',
  'CREDIT_NOTE',
  'QUOTATION',
  'DELIVERY_NOTE',
  'OTHER',
] as const
export type PurchaseDocumentKind = (typeof PURCHASE_DOCUMENT_KINDS)[number]

/** One line as read. Numbers are as written (Latin or Arabic digits, separators); the prefill rules parse them. */
export const purchaseCaptureLineSchema = z.object({
  /** The line's text as written: the item's name on the document. */
  description: z.string().max(300),
  /** The supplier's own item code as written, if any. */
  code: z.string().max(60).nullable(),
  /** The product it is in the company's catalogue (a variant id a lookup returned), or null: not found. */
  variantId: z.uuid().nullable(),
  quantity: z.string().max(30),
  unitPrice: z.string().max(30).nullable(),
  /** A discount on the line as a percentage, as written. */
  discountPercent: z.string().max(10).nullable(),
  /** The VAT rate on the line as a percentage, as written ("5"). */
  taxPercent: z.string().max(10).nullable(),
  /** The line's own total as the document prints it. */
  lineTotal: z.string().max(30).nullable(),
  /** How sure the reader is of the product and the figures, 0 to 1. */
  confidence: z.number().min(0).max(1),
})

/**
 * What was read from a typed list or a supplier's bill: the assistant's answer for `purchasing.capture`
 * (every field present, null when not read, as strict model output needs) and the body of `POST
 * /purchasing/purchases/prefill`. Nothing here is trusted: the prefill rules check every id and number.
 */
export const purchaseCaptureSchema = z.object({
  /** The supplier found in the company's suppliers, or null; and the name as written. */
  supplierId: z.uuid().nullable(),
  supplierName: z.string().max(200).nullable(),
  /**
   * The supplier's VAT (tax) number as written on the bill, or null. Room for any way a bill prints it:
   * the rules compare it with the supplier's own (`PurchasePrefill.supplierTaxCheck`).
   */
  supplierTaxNumber: z.string().max(60).nullable(),
  /** What the paper is (`PURCHASE_DOCUMENT_KINDS`); null on a typed list. */
  documentKind: z.enum(PURCHASE_DOCUMENT_KINDS).nullable(),
  /** The bill's own number. */
  supplierReference: z.string().max(100).nullable(),
  /** YYYY-MM-DD as the reader understood the document's dates; checked by the rules. */
  purchaseDate: z.string().max(10).nullable(),
  dueDate: z.string().max(10).nullable(),
  /**
   * The currency as written ("OMR", "ر.ع.", "Omani Riyals", "الريال العماني"); null when the document names
   * none. Room for any spelling a bill prints: an answer over a limit loses the whole run.
   */
  currency: z.string().max(40).nullable(),
  /** Whether the document's unit prices include VAT; null: it cannot be told. */
  pricesIncludeTax: z.boolean().nullable(),
  /** The document's own totals as written: before VAT, the VAT, and the amount due. */
  net: z.string().max(30).nullable(),
  tax: z.string().max(30).nullable(),
  total: z.string().max(30).nullable(),
  lines: z.array(purchaseCaptureLineSchema).max(PURCHASE_CAPTURE_MAX_LINES),
  /** The document has more lines than were returned. */
  truncated: z.boolean(),
  /** What the reader could not read or was unsure of, in plain sentences. */
  notes: z.array(z.string().max(300)).max(10),
})
export type PurchaseCapture = z.infer<typeof purchaseCaptureSchema>

/** What the prefill rules found about the purchase as a whole (ASSISTANT_CAPTURE.md §3.3). */
export const PURCHASE_PREFILL_WARNINGS = [
  /**
   * The document is a credit note, not a bill: it is filled, but it belongs in a supplier return, which lowers
   * what is owed (decision 28).
   */
  'CREDIT_NOTE',
  /** The document is a quotation, a delivery note or another paper: nothing is owed on it yet. `params.kind`. */
  'NOT_A_BILL',
  /**
   * No supplier was named or matched: the user chooses one. `params.name` and `params.taxNumber`: as written
   * (for a new supplier).
   */
  'SUPPLIER_NOT_FOUND',
  /** The supplier named is inactive or archived: it is not filled. `params.name`. */
  'SUPPLIER_INACTIVE',
  /**
   * The VAT number on the bill is not the chosen supplier's (compared normalised, `supplierTaxCheck`).
   * `params.onBill` and `params.onFile`, each as written.
   */
  'SUPPLIER_TAX_MISMATCH',
  /** The same supplier and bill number is already entered (`duplicates` lists them). */
  'DUPLICATE_BILL',
  /** The lines' total and the document's differ by more than the tolerance. `params.computed`, `params.document`. */
  'TOTAL_MISMATCH',
  /** The document's currency is not OMR. `params.currency`. */
  'FOREIGN_CURRENCY',
  /** The document's prices were on the other VAT basis and were converted to the company's. `params.from`. */
  'PRICE_BASIS_CONVERTED',
  /** The document had more lines than a capture carries. */
  'LINES_TRUNCATED',
  /** No date was read: today is used. */
  'DATE_MISSING',
  /** No line was read at all. */
  'NOTHING_READ',
] as const
export type PurchasePrefillWarning = (typeof PURCHASE_PREFILL_WARNINGS)[number]

/** What the rules found about one line. */
export const PURCHASE_PREFILL_LINE_ISSUES = [
  /** Not matched to a product: the user chooses one, or keeps it a text line. */
  'NO_PRODUCT',
  /** The product named is archived, not the company's or cannot be bought: the line is a text line. */
  'PRODUCT_UNAVAILABLE',
  /** The reader's confidence is under `PURCHASE_PREFILL_LOW_CONFIDENCE`. */
  'LOW_CONFIDENCE',
  /** The quantity could not be read as a number above zero: 1 is used. */
  'QUANTITY_UNREADABLE',
  /** No unit price was read: the printed line total ÷ quantity is used. */
  'PRICE_FROM_TOTAL',
  /**
   * No unit price and no line total were read: the matched product's last purchase cost, else its average
   * cost, as the product picker suggests it (decision 26).
   */
  'PRICE_LAST_COST',
  /** No unit price, no line total, and no product or no cost to go on: 0. */
  'PRICE_MISSING',
  /** A fraction of a product counted in whole units. */
  'WHOLE_UNITS',
  /** Quantity × price (less the discount) is not the line's own total as printed, before or after VAT. */
  'LINE_TOTAL_MISMATCH',
  /**
   * The VAT per cent read is not one of the company's rates: the product's own rate is used, or on a line
   * without a product the company's default rate (none on a document whose VAT was read as zero).
   */
  'TAX_RATE_UNKNOWN',
] as const
export type PurchasePrefillLineIssue = (typeof PURCHASE_PREFILL_LINE_ISSUES)[number]

/** A line whose confidence is under this is marked for checking. */
export const PURCHASE_PREFILL_LOW_CONFIDENCE = 0.6
/** Totals further apart than this (OMR) are a mismatch. */
export const PURCHASE_PREFILL_TOTAL_TOLERANCE = '0.005'

/** A line for the New purchase form. */
export interface PurchasePrefillLine {
  /** The product as the purchase line picker gives it (unit, tax rate, serial tracking, last cost); null: a text line. */
  variant: PurchaseVariantLookup | null
  /** As written on the document. */
  description: string
  code: string | null
  /** Parsed: above zero, up to 4 decimals. */
  quantity: string
  /** Per unit, 3 decimals, on the company's price basis (`PurchasePrefill.pricesIncludeTax`). */
  unitPrice: string
  /** A percentage read from the document is PERCENTAGE; otherwise PER_UNIT with "0". */
  discountType: 'PERCENTAGE' | 'PER_UNIT'
  discountValue: string
  /** The company's tax rate matching the per cent read, else the product's own; null: no tax. */
  taxRateId: string | null
  confidence: number
  issues: PurchasePrefillLineIssue[]
}

/**
 * `POST /purchasing/purchases/prefill` (purchases.create): values for the New purchase form from what was read,
 * by rules. It writes nothing and needs no AI. The user reviews the form and saves through the normal command.
 */
export interface PurchasePrefill {
  /** The supplier, when it is an active supplier of the company. */
  supplier: SupplierOption | null
  /**
   * How `supplier` was found: the reader chose it ('READER'), or the rules by the one active supplier with
   * the VAT number read ('TAX_NUMBER'); null without a supplier.
   */
  supplierMatchedBy: 'READER' | 'TAX_NUMBER' | null
  /**
   * The VAT number read against the supplier's own, normalised (Latin digits; no spaces, dashes or dots; any
   * case); null when either is missing.
   */
  supplierTaxCheck: 'MATCH' | 'MISMATCH' | null
  /** The name as written, for the "not found" message. */
  supplierName: string | null
  /** The date read when it is a real date, else today (Asia/Muscat). */
  purchaseDate: string
  dueDate: string | null
  supplierReference: string | null
  /** The company's price basis, which the lines' unit prices are on. */
  pricesIncludeTax: boolean
  lines: PurchasePrefillLine[]
  /** The document's own figures, parsed (3 dp); null when not read. */
  document: { net: string | null; tax: string | null; total: string | null; currency: string | null }
  /** What a purchase with these lines totals, as the create command calculates it. */
  totals: { net: string; tax: string; total: string }
  warnings: { code: PurchasePrefillWarning; params: Record<string, string | number | null> }[]
  /** Purchases of the same supplier with the same bill number (the form's own duplicate check). */
  duplicates: PurchaseDuplicate[]
  /** The reader's notes, as plain text. */
  notes: string[]
}

export interface PurchaseListItem {
  id: string
  /** Drafts: the latest approval run's status (ADR-018), null when never submitted. List only. */
  approvalStatus?: WorkflowRunStatus | null
  /** PO-2026-0001, assigned when posted. */
  number: string | null
  status: InvoiceStatus
  paymentStatus: PaymentStatus | null
  /**
   * Null on a draft saved without a supplier (G25; posted purchases always have one), and for a reader
   * without `suppliers.read`, who sees `supplierHidden: true` instead (G26).
   */
  supplier: SupplierRef | null
  /** The supplier is withheld from this reader (no `suppliers.read`). Always sent by the API. */
  supplierHidden?: boolean
  /**
   * A supplier is set, told to every reader, those it is hidden from included: without one the purchase
   * cannot be posted (422 SUPPLIER_REQUIRED, G25). Always sent by the API; optional in the type only so
   * older fixtures still compile.
   */
  hasSupplier?: boolean
  purchaseDate: string
  dueDate: string
  supplierReference: string | null
  total: string
  /**
   * What settles the purchase: Σ allocations of supplier payments **and** supplier returns (the
   * "Settled" figure, PO-R2). The two parts are `settledByPayments` and `settledByReturns`.
   */
  amountPaid: string
  balance: string
  /**
   * The part of `amountPaid` settled by supplier payments (what the print shows as "Paid"). Always sent
   * by the API; optional in the type only so older fixtures still compile (ASM-095 (1)).
   */
  settledByPayments?: string
  /** The part of `amountPaid` settled by supplier returns and allowances ("Returned"). */
  settledByReturns?: string
  /** Days past the due date (today in Asia/Muscat) when `paymentStatus` is OVERDUE; null otherwise. */
  daysOverdue?: number | null
  /** The user who created the purchase; null for migrated and system purchases. */
  createdBy?: UserRef | null
  /**
   * The requesting org unit; null when none. Always sent by the API; optional in the type only so
   * older fixtures still compile.
   */
  orgUnit?: OrgUnitRef | null
  /**
   * The projects its lines are tagged to (the list's Project column), by code; empty when none.
   * Always sent by the API; optional in the type only so older fixtures still compile.
   */
  projects?: CostObjectProjectRef[]
}

export interface PurchaseLine extends DocumentLine {
  /** The catalogue product's type; null for a free-text line (never stocked, like a service). */
  productType: ProductType | null
  expenseAccountId: string | null
  /** Quantity already returned on posted supplier returns. */
  returnedQuantity: string
  /**
   * What can still go back to the supplier, in the line's unit: purchased − returned on posted supplier
   * returns − goods brought back from the project's site into stock (never below zero). Always sent by
   * the API; optional in the type only so older fixtures still compile (then purchased − returned).
   */
  returnableQuantity?: string
  /** Serial numbers (serial-tracked products; empty otherwise). Always sent by the API; optional in the type only until the web fixtures carry it. */
  serials?: string[]
  /** The product tracks serial numbers: its lines carry one per unit. Always sent by the API; optional in the type only until the web fixtures carry it. */
  trackSerials?: boolean
  /** The project tag; null when none. Always sent by the API; optional in the type only for older fixtures. */
  projectTag?: CostObjectTag | null
}

export interface PurchaseAllocation {
  id: string
  sourceType: 'PAYMENT' | 'SUPPLIER_RETURN'
  sourceId: string
  sourceNumber: string
  amount: string
  allocatedOn: string
}

export interface Purchase extends PurchaseListItem {
  /**
   * Approval workflow state (ADR-018): whether a workflow applies, the latest run, and whether posting
   * is allowed now. Optional in the type only until the web fixtures carry it; the API always sends it.
   */
  approval?: DocumentApproval
  warehouse: { id: string; code: string } | null
  pricesIncludeTax: boolean
  notes: string | null
  gross: string
  discount: string
  subtotal: string
  tax: string
  lines: PurchaseLine[]
  /** Settlements: supplier payments and returns applied to this purchase. */
  allocations: PurchaseAllocation[]
  journalEntryId: string | null
  voidReason: string | null
  version: number
  createdAt: string
  updatedAt: string
  /** When it was posted; null for a draft. Detail only (optional for older fixtures). */
  postedAt?: string | null
  /**
   * Supplier returns and allowances issued against this purchase (the trail), drafts included, void
   * ones not; oldest first. `applied` is what each applied to this purchase (a return may leave credit
   * with the supplier or settle other purchases). Detail only.
   */
  returns?: {
    id: string
    kind: SupplierReturnKind
    number: string | null
    status: 'DRAFT' | 'POSTED'
    returnDate: string
    total: string
    applied: string
  }[]
  /** Detail only. */
  voidedAt?: string | null
  voidedBy?: UserRef | null
}

// ── supplier payments ──────────────────────────────────────────────────────────

export const apAllocationInputSchema = z.object({
  purchaseId: z.uuid(),
  amount: positiveAmount,
})

export const supplierPaymentInputSchema = z.object({
  supplierId: z.uuid(),
  paymentDate: z.iso.date(),
  treasuryAccountId: z.uuid(),
  method: paymentMethodSchema,
  /** The company's own method (its type must be `method`); null: the plain type. */
  paymentMethodId: paymentMethodIdSchema,
  amount: positiveAmount,
  reference: optionalText(100),
  notes: optionalText(500),
  allocations: z.array(apAllocationInputSchema).max(200).default([]),
  /**
   * Method CHEQUE (ADR-046): the cheque written, followed up until the bank pays it out (the payment then
   * credits "Cheques issued, not presented", not the bank). Left out: paid from the account at once.
   */
  cheque: issuedChequeInputSchema.nullish().transform((v) => v ?? null),
})
export type SupplierPaymentInput = z.input<typeof supplierPaymentInputSchema>

export const apAllocateSchema = z.object({ allocations: z.array(apAllocationInputSchema).min(1).max(200) })
export type ApAllocateInput = z.input<typeof apAllocateSchema>

/**
 * `GET /purchasing/payments`. `q` matches the number and reference, the supplier's code, and the
 * supplier's name and Arabic name with Arabic folding. Sort keys: SUPPLIER_PAYMENT_SORT_FIELDS
 * (default `paymentDate`).
 */
export const supplierPaymentListQuerySchema = pageQuerySchema.extend({
  supplierId: z.uuid().optional(),
  status: z.enum(['POSTED', 'VOID']).optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  /** Posted payments with credit left to refund or allocate (amount − allocated − refunded > 0). */
  hasOpenCredit: z.stringbool().optional(),
  /** Payments made by this method. */
  method: paymentMethodSchema.optional(),
  /** Payments made from this cash or bank account (the daily cash-out check). */
  treasuryAccountId: z.uuid().optional(),
  /** Payments recorded by this user; `me` is the caller. */
  createdBy: createdByFilterSchema,
})
export type SupplierPaymentListQuery = z.input<typeof supplierPaymentListQuerySchema>

/** `unallocated` is the advance left (0 for void payments); `supplier` the supplier's name. */
export const SUPPLIER_PAYMENT_SORT_FIELDS = [
  'paymentDate',
  'number',
  'amount',
  'unallocated',
  'supplier',
] as const

/**
 * The payment list's views: `all`, `withCredit` (`hasOpenCredit=true`, the advances), `void`
 * (`status=VOID`).
 */
export const SUPPLIER_PAYMENT_VIEWS = ['all', 'withCredit', 'void'] as const
export type SupplierPaymentView = (typeof SUPPLIER_PAYMENT_VIEWS)[number]

/** `GET /purchasing/payments/summary`: the list's filters other than the view, paging and sort. */
export const supplierPaymentSummaryQuerySchema = supplierPaymentListQuerySchema.pick({
  q: true,
  supplierId: true,
  from: true,
  to: true,
  method: true,
  treasuryAccountId: true,
  createdBy: true,
})
export type SupplierPaymentSummaryQuery = z.input<typeof supplierPaymentSummaryQuerySchema>

/**
 * Counts per view (one grouped query); each `count` equals the `total` of that view's list call.
 * `all.amount` is Σ amount of the POSTED payments among them; `withCredit.unallocated` Σ of that
 * view's `unallocated`.
 */
export interface SupplierPaymentSummary {
  all: { count: number; amount: string }
  withCredit: { count: number; unallocated: string }
  void: { count: number }
}

/** `GET /purchasing/payments/export` (purchasing.export): the list's filters, view and sort, as CSV. */
export const supplierPaymentExportQuerySchema = supplierPaymentListQuerySchema
  .omit({ page: true, pageSize: true })
  .extend(exportFields)
export type SupplierPaymentExportQuery = z.input<typeof supplierPaymentExportQuerySchema>

/** A settlement seen from its source (payment or return): which purchase, how much. */
export interface ApSourceAllocation {
  id: string
  purchaseId: string
  purchaseNumber: string
  amount: string
  allocatedOn: string
}

export interface SupplierPayment {
  id: string
  /** PV-2026-0001 (supplier payment voucher), yearly series by payment date. */
  number: string
  paymentDate: string
  supplier: SupplierRef
  treasuryAccount: { id: string; name: string }
  method: PaymentMethod
  /** The company's own method (ADR-047), or null. Optional only for older fixtures. */
  paymentMethod?: PaymentMethodRef | null
  amount: string
  allocated: string
  /** Paid back by the supplier through supplier refunds. */
  refunded: string
  /** The credit left with the supplier on this payment: amount − allocated − refunded. */
  unallocated: string
  reference: string | null
  notes: string | null
  status: 'POSTED' | 'VOID'
  voidReason: string | null
  /** The cheque it was paid with, followed up (ADR-046). Always sent by the API; optional for older fixtures. */
  cheque?: DocumentChequeRef | null
  allocations: ApSourceAllocation[]
  createdAt: string
  /**
   * The user who recorded the payment (the voucher's "Prepared by"); null for migrated and system
   * payments. Always sent by the API (list and detail); optional for older fixtures.
   */
  createdBy?: UserRef | null
  /** The payment's journal entry while it stands; null once voided (PU-68). Detail only. */
  journalEntryId?: string | null
  /** Refunds the supplier paid back out of this payment's advance (the trail). Detail only. */
  refunds?: DrawnRefund[]
  /** Detail only. */
  voidedAt?: string | null
  voidedBy?: UserRef | null
}

// ── supplier returns and allowances ────────────────────────────────────────────

/**
 * Supplier returns (legacy debit notes / purchase returns) reduce what we owe a supplier.
 *   RETURN     goods (or services) going back from a posted purchase: lines point at purchase lines
 *              and take their prices, discounts and tax from them; stock leaves at its purchase cost.
 *   ALLOWANCE  a price reduction the supplier grants, optionally against a purchase; no stock.
 */
export const supplierReturnKindSchema = z.enum(['RETURN', 'ALLOWANCE'])
export type SupplierReturnKind = z.infer<typeof supplierReturnKindSchema>

export const supplierReturnLineInputSchema = z.object({
  purchaseLineId: z.uuid(),
  quantity: positiveQuantity,
  /** Serial-tracked products: the serials going back, each IN_STOCK at the return's warehouse. */
  serials: lineSerialsSchema,
})

export const supplierAllowanceLineInputSchema = z
  .object({
    description: z.string().trim().min(1, 'Describe the line').max(500),
    quantity: positiveQuantity,
    unitPrice: moneySchema.refine((v) => Number(v) >= 0, 'Price cannot be negative'),
    discountType: discountTypeSchema.default('PER_UNIT'),
    discountValue: moneySchema.default('0'),
    /** null: no tax. */
    taxRateId: z.uuid().nullable(),
    /** The project whose cost the allowance reduces (credits its cost account). */
    ...projectTagFields,
  })
  .superRefine(refineProjectTag)

const returnHeader = {
  supplierId: z.uuid(),
  returnDate: z.iso.date(),
  reason: z.string().trim().min(1, 'Give a reason').max(500),
  /**
   * The supplier's credit-note number (RT-R3): the document that supports reducing input VAT.
   * Searched, listed and printed.
   */
  supplierReference: optionalText(100),
  notes: optionalText(2000),
}

const supplierReturn = z.object({
  kind: z.literal('RETURN'),
  ...returnHeader,
  purchaseId: z.uuid(),
  /** Where the returned goods leave from (default: the warehouse the purchase received into). */
  warehouseId: optionalId,
  lines: z.array(supplierReturnLineInputSchema).min(1, 'Add at least one line').max(500),
})

const supplierAllowance = z.object({
  kind: z.literal('ALLOWANCE'),
  ...returnHeader,
  purchaseId: optionalId,
  lines: z.array(supplierAllowanceLineInputSchema).min(1, 'Add at least one line').max(500),
})

export const supplierReturnInputSchema = z.discriminatedUnion('kind', [supplierReturn, supplierAllowance])
export type SupplierReturnInput = z.input<typeof supplierReturnInputSchema>
export const supplierReturnUpdateSchema = z.discriminatedUnion('kind', [
  supplierReturn.extend({ version: versionSchema }),
  supplierAllowance.extend({ version: versionSchema }),
])
export type SupplierReturnUpdate = z.input<typeof supplierReturnUpdateSchema>

/**
 * `GET /purchasing/returns`. `q` matches the number, the supplier's credit-note number, the reason and
 * the purchase number, the supplier's code, and the supplier's name and Arabic name with Arabic
 * folding. Sort keys: SUPPLIER_RETURN_SORT_FIELDS (default `returnDate`).
 */
export const supplierReturnListQuerySchema = pageQuerySchema.extend({
  kind: supplierReturnKindSchema.optional(),
  status: z.enum(['DRAFT', 'POSTED', 'VOID']).optional(),
  supplierId: z.uuid().optional(),
  purchaseId: z.uuid().optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  /** Posted returns with credit left to refund or allocate (total − allocated − refunded > 0). */
  hasOpenCredit: z.stringbool().optional(),
  /** Returns created by this user; `me` is the caller. */
  createdBy: createdByFilterSchema,
})
export type SupplierReturnListQuery = z.input<typeof supplierReturnListQuerySchema>

export const SUPPLIER_RETURN_SORT_FIELDS = [
  'returnDate',
  'number',
  'total',
  'unallocated',
  'supplier',
] as const

/**
 * The return list's views: `all`, `drafts` (`status=DRAFT`), `withCredit` (`hasOpenCredit=true`),
 * `void` (`status=VOID`).
 */
export const SUPPLIER_RETURN_VIEWS = ['all', 'drafts', 'withCredit', 'void'] as const
export type SupplierReturnView = (typeof SUPPLIER_RETURN_VIEWS)[number]

/** `GET /purchasing/returns/summary`: the list's filters other than the view, paging and sort. */
export const supplierReturnSummaryQuerySchema = supplierReturnListQuerySchema.pick({
  q: true,
  kind: true,
  supplierId: true,
  purchaseId: true,
  from: true,
  to: true,
  createdBy: true,
})
export type SupplierReturnSummaryQuery = z.input<typeof supplierReturnSummaryQuerySchema>

/** Counts per view; each `count` equals the `total` of that view's list call. */
export interface SupplierReturnSummary {
  all: { count: number }
  drafts: { count: number }
  withCredit: { count: number; unallocated: string }
  void: { count: number }
}

/** `GET /purchasing/returns/export` (purchasing.export): the list's filters, view and sort, as CSV. */
export const supplierReturnExportQuerySchema = supplierReturnListQuerySchema
  .omit({ page: true, pageSize: true })
  .extend(exportFields)
export type SupplierReturnExportQuery = z.input<typeof supplierReturnExportQuerySchema>

export interface SupplierReturnListItem {
  id: string
  kind: SupplierReturnKind
  /** DN-000001 (legacy debit-note format), assigned when posted. */
  number: string | null
  status: 'DRAFT' | 'POSTED' | 'VOID'
  supplier: SupplierRef
  purchase: { id: string; number: string | null } | null
  returnDate: string
  total: string
  /** Applied to purchases. The rest is credit with the supplier. */
  allocated: string
  /** Paid back by the supplier through supplier refunds. */
  refunded: string
  /** The credit left with the supplier on this return: total − allocated − refunded. */
  unallocated: string
  /** The supplier's credit-note number. Always sent; optional for older fixtures. */
  supplierReference?: string | null
  /** The user who created the return; null for migrated and system returns. */
  createdBy?: UserRef | null
}

export interface SupplierReturnLine {
  id: string
  lineNo: number
  purchaseLineId: string | null
  variantId: string | null
  description: string
  quantity: string
  /** A return line keeps its purchase line's unit. Always sent by the API; optional for older fixtures. */
  unit?: LineUnit | null
  unitFactor?: string
  unitPrice: string
  discountType: 'PERCENTAGE' | 'PER_UNIT' | 'LINE_AMOUNT'
  discountValue: string
  taxRateId: string | null
  taxRate: string
  discount: string
  net: string
  tax: string
  total: string
  /** Serial numbers (serial-tracked products; empty otherwise). Always sent by the API; optional in the type only until the web fixtures carry it. */
  serials?: string[]
  /**
   * The project tag: a RETURN line copies its purchase line's (read-only); an ALLOWANCE line has its
   * own. Always sent by the API; optional in the type only for older fixtures.
   */
  projectTag?: CostObjectTag | null
}

export interface SupplierReturn extends SupplierReturnListItem {
  warehouse: { id: string; code: string } | null
  pricesIncludeTax: boolean
  reason: string
  notes: string | null
  gross: string
  discount: string
  subtotal: string
  tax: string
  lines: SupplierReturnLine[]
  allocations: ApSourceAllocation[]
  journalEntryId: string | null
  voidReason: string | null
  version: number
  createdAt: string
  updatedAt: string
  /** Refunds the supplier paid back out of this return's credit (the trail). Detail only. */
  refunds?: DrawnRefund[]
  /** Detail only. */
  voidedAt?: string | null
  voidedBy?: UserRef | null
}

// ── supplier refunds ───────────────────────────────────────────────────────────

/**
 * Supplier credit paid back to us (SRF-2026-0001): cash or a bank transfer from a supplier out of an
 * advance (a payment's unallocated remainder) or an unapplied supplier return. Posts Dr the treasury
 * account's GL / Cr payables (supplier).
 */
export const supplierCreditSourceTypeSchema = z.enum(['PAYMENT', 'SUPPLIER_RETURN'])
export type SupplierCreditSourceType = z.infer<typeof supplierCreditSourceTypeSchema>

export const supplierRefundApplicationInputSchema = z.object({
  sourceType: supplierCreditSourceTypeSchema,
  sourceId: z.uuid(),
  amount: positiveAmount,
})

/** The refund's amount is the sum of the credit it consumes. */
export const supplierRefundInputSchema = z
  .object({
    supplierId: z.uuid(),
    refundDate: z.iso.date(),
    /** Where the money arrives. */
    treasuryAccountId: z.uuid(),
    method: paymentMethodSchema,
    paymentMethodId: paymentMethodIdSchema,
    /**
     * By cheque, the cheque's number, and required: a refund cheque stays outside the cheque register
     * (ASM-254), so the reference is all that names it (as on a customer refund).
     */
    reference: optionalText(100),
    notes: optionalText(500),
    applications: z
      .array(supplierRefundApplicationInputSchema)
      .min(1, 'Choose the credit to refund')
      .max(200),
  })
  .superRefine((v, ctx) => {
    if (v.method === 'CHEQUE' && !v.reference) {
      ctx.addIssue({ code: 'custom', path: ['reference'], message: 'Enter the cheque number' })
    }
  })
export type SupplierRefundInput = z.input<typeof supplierRefundInputSchema>

/**
 * `GET /purchasing/refunds`. `q` matches the number and reference, the supplier's code, and the
 * supplier's name and Arabic name with Arabic folding. Sort keys: SUPPLIER_REFUND_SORT_FIELDS.
 */
export const supplierRefundListQuerySchema = pageQuerySchema.extend({
  supplierId: z.uuid().optional(),
  status: z.enum(['POSTED', 'VOID']).optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  /** Refunds received by this method. */
  method: paymentMethodSchema.optional(),
  /** Refunds received into this cash or bank account. */
  treasuryAccountId: z.uuid().optional(),
  /** Refunds recorded by this user; `me` is the caller. */
  createdBy: createdByFilterSchema,
})
export type SupplierRefundListQuery = z.input<typeof supplierRefundListQuerySchema>

export const SUPPLIER_REFUND_SORT_FIELDS = ['refundDate', 'number', 'amount', 'supplier'] as const

/** The refund list's views: `all`, `void` (`status=VOID`). */
export const SUPPLIER_REFUND_VIEWS = ['all', 'void'] as const
export type SupplierRefundView = (typeof SUPPLIER_REFUND_VIEWS)[number]

/** `GET /purchasing/refunds/summary`: the list's filters other than the view, paging and sort. */
export const supplierRefundSummaryQuerySchema = supplierRefundListQuerySchema.pick({
  q: true,
  supplierId: true,
  from: true,
  to: true,
  method: true,
  treasuryAccountId: true,
  createdBy: true,
})
export type SupplierRefundSummaryQuery = z.input<typeof supplierRefundSummaryQuerySchema>

/** `all.amount` is Σ amount of the POSTED refunds among them (a void refund received nothing). */
export interface SupplierRefundSummary {
  all: { count: number; amount: string }
  void: { count: number }
}

/** `GET /purchasing/refunds/export` (purchasing.export): the list's filters, view and sort, as CSV. */
export const supplierRefundExportQuerySchema = supplierRefundListQuerySchema
  .omit({ page: true, pageSize: true })
  .extend(exportFields)
export type SupplierRefundExportQuery = z.input<typeof supplierRefundExportQuerySchema>

export interface SupplierRefundListItem {
  id: string
  /** SRF-2026-0001, yearly series by refund date. */
  number: string
  refundDate: string
  supplier: SupplierRef
  treasuryAccount: { id: string; name: string }
  method: PaymentMethod
  /** The company's own method (ADR-047), or null. Optional only for older fixtures. */
  paymentMethod?: PaymentMethodRef | null
  amount: string
  reference: string | null
  status: 'POSTED' | 'VOID'
  /**
   * The user who recorded the refund (the receipt voucher's "Received by"); null for migrated and
   * system refunds. Always sent by the API; optional for older fixtures.
   */
  createdBy?: UserRef | null
  /**
   * Where the money came from (RF-H1): the payments and returns it drew on, oldest application first.
   * Empty for a void refund (its credit went back). List only; the detail has `applications`.
   */
  sources?: {
    sourceType: SupplierCreditSourceType
    sourceId: string
    sourceNumber: string
    amount: string
  }[]
}

export interface SupplierRefund extends SupplierRefundListItem {
  notes: string | null
  voidReason: string | null
  applications: {
    id: string
    sourceType: SupplierCreditSourceType
    sourceId: string
    sourceNumber: string
    amount: string
  }[]
  journalEntryId: string | null
  createdAt: string
  /** Detail only. */
  voidedAt?: string | null
  voidedBy?: UserRef | null
}

// ── payables reports ───────────────────────────────────────────────────────────

/**
 * The supplier's lines on the payables account. Balances are credit-positive: a positive balance is
 * what we owe the supplier, a negative one is credit we hold with them.
 */
/**
 * What a supplier statement line comes from: a purchase, a supplier payment, a supplier return or
 * allowance, or a supplier refund (manual journals cannot touch the payables account). OTHER is a
 * safety net for a future source. The opening balance is the statement's `opening`, never a line.
 */
export const SUPPLIER_STATEMENT_SOURCE_KINDS = ['PURCHASE', 'PAYMENT', 'RETURN', 'REFUND', 'OTHER'] as const
export type SupplierStatementSourceKind = (typeof SUPPLIER_STATEMENT_SOURCE_KINDS)[number]

export interface SupplierStatement {
  supplier: SupplierRef
  from: string
  to: string
  opening: string
  lines: {
    date: string
    journalEntryId: string
    journalNumber: string
    sourceType: string | null
    sourceId: string | null
    /** Stable kind of the source document, for localized descriptions. */
    sourceKind: SupplierStatementSourceKind
    /** The source document's number. */
    sourceNumber: string | null
    /** The line reverses an earlier one: its source document was voided. */
    reversal: boolean
    /** Built by the server in English; the UI should prefer `sourceKind` + `sourceNumber`. */
    description: string | null
    debit: string
    credit: string
    balance: string
  }[]
  closing: string
}

export interface ApAgingRow {
  supplier: SupplierRef
  current: string
  days1to30: string
  days31to60: string
  days61to90: string
  over90: string
  /** Open purchase balances minus unallocated payments and returns (what we owe). */
  total: string
  unallocatedCredit: string
}

// ── supplier account (the 360 profile) ─────────────────────────────────────────

/**
 * `GET /suppliers/:id/summary` (purchasing.read): the supplier profile's figures in one call (X-6).
 * Each figure uses the definition of the screen it links to:
 *   balance, overdue, credit  the payables aging (`GET /purchasing/aging?supplierId`), equal to the
 *                             statement closing balance; credit-positive (positive: we owe them)
 *   purchasesThisYear         posted purchases − posted returns, ex-VAT (the purchases-by-supplier
 *                             report's `netPurchases`)
 *   counts                    the list filters of purchases (`open`, `paymentStatus=OVERDUE`, drafts),
 *                             payments and returns (`hasOpenCredit`), refunds
 */
export interface SupplierAccountSummary {
  supplier: SupplierRef
  asOf: string
  /** The aging total = the statement closing balance: open purchases − unallocated credit. */
  balance: string
  /** Open purchase balances past their due date (before credit). */
  overdue: string
  /** The oldest aging bucket holding an overdue amount; null when nothing is overdue. */
  oldestOverdueBucket: AgingBucket | null
  /** Credit held with the supplier: posted payments and returns not allocated and not refunded. */
  unallocatedCredit: string
  /** Latest posted purchase by purchase date. */
  lastPurchase: {
    id: string
    number: string
    purchaseDate: string
    supplierReference: string | null
    total: string
  } | null
  /** Latest posted supplier payment by payment date. */
  lastPayment: { id: string; number: string; paymentDate: string; amount: string } | null
  /** From the first day of the fiscal year containing `asOf` (1 January without one) to `asOf`. */
  purchasesThisYear: { from: string; to: string; netPurchases: string; purchaseCount: number }
  counts: {
    /** `/purchasing/purchases?supplierId`; `open`: `…&open=true`; `overdue`: `…&paymentStatus=OVERDUE`. */
    purchases: { all: number; open: number; overdue: number; draft: number }
    /** `/purchasing/payments?supplierId`; `openCredit`: `…&hasOpenCredit=true`. */
    payments: { all: number; openCredit: number }
    /** `/purchasing/returns?supplierId`; `draft`: `…&status=DRAFT`; `openCredit`: `…&hasOpenCredit=true`. */
    returns: { all: number; draft: number; openCredit: number }
    /** `/purchasing/refunds?supplierId`. */
    refunds: number
  }
  attention: {
    overduePurchases: number
    draftPurchases: number
    draftReturns: number
    /** Payments and returns with credit left to allocate or recover. */
    openCreditSources: number
  }
}
